import { FilterPanel } from 'views/components';
import PropTypes from 'prop-types';
import React from 'react';
import { config } from 'config';
import { dateToObject } from 'shared/utils';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import {
    AreaPicker,
    CostCenterPicker,
    LicenseReasonPicker,
    PatientPicker,
    PositionPicker,
    QuizPicker,
    WorkplacePicker
} from 'views/pickers';
import {
    Col,
    Row
} from 'reactstrap';
import {
    DateSelect,
    FormGroup,
    Input
} from 'mol-ui';
import {
    useObjectState,
    useSelector,
    useToggle
} from 'hooks';
const {
    areas,
    costCenters,
    diagnosisLicense,
    positions,
    workplaces
} = config;

const KEYS = {
    AREA: 'area',
    COST_CENTER: 'cost_center',
    DATE: 'date',
    HR_HEAD: 'hr_head',
    LICENSE_REASON: 'license_reason',
    PATIENT: 'patient',
    POSITION: 'position',
    PSYCHOSOCIAL_RISK: 'psychosocial_risk',
    QUIZ: 'quiz',
    WORKPLACE: 'workplace'
};

const PatientFilters = ({
    displayFilters,
    onFiltersChange = () => null
}) => {

    const DEFAULT_FILTERS = {
        area_id: null,
        cost_center_id: null,
        date: (typeof displayFilters === 'object') && displayFilters.includes('date') || displayFilters === true ? dateToObject(moment()) : null,
        hr_head: '',
        license_reason_id: null,
        patient_id: null,
        position_id: null,
        survey_id: null,
        workplace_id: null
    };

    const { t } = useTranslation();

    const [filters, setFilters, setFiltersValue] = useObjectState({ ...DEFAULT_FILTERS });
    const [showFilters, toggleFilters] = useToggle();
    const { loading } = useSelector('statistics');

    const handleClear = () => {
        setFilters({ ...DEFAULT_FILTERS });
        onFiltersChange({ ...DEFAULT_FILTERS });
    };

    const displayFilter = key => {
        if (typeof displayFilters === 'boolean') return displayFilters;
        if (typeof displayFilters !== 'object') return false;
        return displayFilters.includes(key);
    };
    
    return (
        <FilterPanel
            active={ showFilters }
            loading={ loading }
            onApply={ () => onFiltersChange(filters) }
            onClear={ handleClear }
            onToggle={ toggleFilters }
        >
            <Row>
                {
                    displayFilter(KEYS.PATIENT) &&
                    <Col sm="3" xs="12">
                        <FormGroup label={ t('Paciente') }>
                            <PatientPicker
                                allowNone={ true }
                                nullPlaceholder={ t('Todos los pacientes') }
                                value={ filters?.patient_id }
                                onChange={ value => setFiltersValue('patient_id', value) }
                            />
                        </FormGroup>
                    </Col>
                }
                {
                    displayFilter(KEYS.WORKPLACE) && workplaces &&
                    <Col sm="3" xs="12">
                        <FormGroup label={ t('Lugar de trabajo') }>
                            <WorkplacePicker
                                allowNone
                                nullPlaceholder={ t('Todos los lugares de trabajo') }
                                value={ filters?.workplace_id }
                                onChange={ value => setFiltersValue('workplace_id', value) }
                            />
                        </FormGroup>
                    </Col>
                }
                {
                    displayFilter(KEYS.POSITION) && positions &&
                    <Col sm="3" xs="12">
                        <FormGroup label={ t('Cargo') }>
                            <PositionPicker
                                allowNone
                                nullPlaceholder={ t('Todos los cargos') }
                                value={ filters?.position_id }
                                onChange={ value => setFiltersValue('position_id', value) }
                            />
                        </FormGroup>
                    </Col>
                }
                {
                    displayFilter(KEYS.AREA) && areas &&
                    <Col sm="3" xs="12">
                        <FormGroup label={ t('Zona') }>
                            <AreaPicker
                                allowNone
                                nullPlaceholder={ t('Todas las zonas') }
                                value={ filters?.area_id }
                                onChange={ value => setFiltersValue('area_id', value) }
                            />
                        </FormGroup>
                    </Col>
                }
                {
                    displayFilter(KEYS.COST_CENTER) && costCenters &&
                    <Col sm="3" xs="12">
                        <FormGroup label={ t('Centro de costos') }>
                            <CostCenterPicker
                                allowNone
                                nullPlaceholder={ t('Todos los centros de costos') }
                                value={ filters?.cost_center_id }
                                onChange={ value => setFiltersValue('cost_center_id', value) }
                            />
                        </FormGroup>
                    </Col>
                }
                {
                    displayFilter(KEYS.HR_HEAD) &&
                    <Col sm="3" xs="12">
                        <FormGroup label={ t('Jefe de RRHH') }>
                            <Input
                                value={ filters?.hr_head }
                                onChange={ value => setFiltersValue('hr_head', value) }
                            />
                        </FormGroup>
                    </Col>
                }
                {
                    displayFilter(KEYS.LICENSE_REASON) && diagnosisLicense &&
                    <Col sm="3" xs="12">
                        <FormGroup label={ t('Motivo de licencia') }>
                            <LicenseReasonPicker
                                allowNone={ true }
                                placeholder={ t('Todos los motivos') }
                                value={ filters?.license_reason_id }
                                onChange={ value => setFiltersValue('license_reason_id', value) }
                            />
                        </FormGroup>
                    </Col>
                }
                {
                    (displayFilter(KEYS.QUIZ) || displayFilter(KEYS.PSYCHOSOCIAL_RISK)) &&
                    <Col sm="3" xs="12">
                        <FormGroup label={ t('Encuesta') }>
                            <QuizPicker
                                allowNone
                                type={ displayFilter(KEYS.QUIZ) ? 'quiz' : 'psychosocial_risk' }
                                value={ filters?.survey_id }
                                onChange={ value => setFiltersValue('survey_id', value) }
                            />
                        </FormGroup>
                    </Col>
                }
                {
                    displayFilter(KEYS.DATE) &&
                    <Col sm="6" xs="12">
                        <FormGroup label={ t('Mes y año') }>
                            <DateSelect
                                hideDays={ true }
                                margin="0px 0px 20px 0px"
                                value={ filters?.date }
                                onChange={ value => setFiltersValue('date', value) }
                            />
                        </FormGroup>
                    </Col>
                }
            </Row>
        </FilterPanel>
    );
};

PatientFilters.propTypes = {
    displayFilters: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
    onFiltersChange: PropTypes.func
};

export default PatientFilters;
